import { toast } from "react-hot-toast";
import { useSelector } from "react-redux";
import { useMutation } from "@tanstack/react-query";
import { useQueryClient } from "@tanstack/react-query";

import { createAxiosInstance } from "api/axiosConfig";
import { isCommonResultsTab } from "features/results/constants/messageTypeGroups";
import { useHandleNetworkError } from "utils/useHandleNetworkError";

const isRadiologyTab = (resultType) =>
  resultType === "radiology" || resultType === "radiology-unmatched";

const getUploadEndpoint = (resultType) => {
  if (isRadiologyTab(resultType)) return "healthlink/radiology/manual/upload";
  // All common-backed tabs (All Results + the message-type filtered tabs)
  // upload through the common endpoint.
  if (isCommonResultsTab(resultType)) return "healthlink/common/manual/upload";

  return "healthlink/laboratory/manual/upload";
};

const getQueryKeysToInvalidate = (resultType) => {
  if (isRadiologyTab(resultType))
    return ["staffRadiologyResults", "unmatchedStaffRadiologyResults"];
  if (isCommonResultsTab(resultType))
    return ["staffCommonResults", "unmatchedStaffCommonResults"];

  return ["staffLaboratoryResults", "unmatchedStaffLaboratoryResults"];
};

export const useUploadManualResults = (onSuccess) => {
  const queryClient = useQueryClient();

  const handleNetworkError = useHandleNetworkError("Error uploading results");

  const tenantVal =
    useSelector((state) => state.auth.user?.tenant) ||
    localStorage.getItem("kinesin-tenant");

  return useMutation({
    mutationFn: async ({ files, resultType }) => {
      const axiosInstance = createAxiosInstance();
      const formData = new FormData();

      Array.from(files).forEach((file) => {
        formData.append("files", file);
      });

      const endpoint = getUploadEndpoint(resultType);
      const response = await axiosInstance.put(
        `${tenantVal}/${endpoint}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      return { data: response.data, resultType };
    },
    onSuccess: ({ data, resultType }) => {
      const { totalMessagesProcessed, successfullyProcessedFiles, filesWithErrors } = data;
      
      if (filesWithErrors && Object.keys(filesWithErrors).length > 0) {
        toast.error(
          `Processed ${totalMessagesProcessed} messages. ${Object.keys(filesWithErrors).length} file(s) had errors.`,
          { duration: 5000 }
        );
      } else {
        toast.success(
          `Successfully uploaded and processed ${totalMessagesProcessed} result(s) from ${successfullyProcessedFiles.length} file(s)!`
        );
      }
      
      const queryKeys = getQueryKeysToInvalidate(resultType);
      queryKeys.forEach((key) => {
        queryClient.invalidateQueries({ queryKey: [key] });
      });
      onSuccess?.();
    },
    onError: handleNetworkError,
  });
};

